import { useRef, useState } from 'react';
import type { OperatorAlertSnapshot } from '@einhaus/project314-types';
import Button from '@/components/UI/Button';

export const OperatorAlertDetails = ({ snapshot }: { snapshot: OperatorAlertSnapshot }) => {
    const [copyStatus, setCopyStatus] = useState('');
    const [manualCopyText, setManualCopyText] = useState<string | null>(null);
    const manualCopyRef = useRef<HTMLTextAreaElement>(null);

    const copy = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setManualCopyText(null);
            setCopyStatus(`${label} copied.`);
        } catch {
            setManualCopyText(text);
            setCopyStatus('Clipboard access was blocked. Select the text below and copy it.');
        }
    };

    return (
        <div className="space-y-5">
            <div>
                <p className="text-sm uppercase tracking-wide text-red-300">
                    {snapshot.severity} · {snapshot.topic}
                </p>
                <h2 className="mt-2 break-words text-2xl font-semibold">{snapshot.title}</h2>
                <p className="mt-2 text-sm text-zinc-400">
                    {snapshot.host} · {snapshot.environment} ·{' '}
                    {new Date(snapshot.occurredAt * 1000).toLocaleString('en-US', { timeZone: 'America/New_York', timeZoneName: 'short' })}
                </p>
            </div>
            <div className="flex flex-wrap gap-3">
                <Button onClick={() => void copy(snapshot.errorText, 'Error')}>Copy error</Button>
                <Button variant="secondary" onClick={() => void copy(snapshot.diagnosticText, 'Diagnostics')}>
                    Copy for LLM
                </Button>
            </div>
            <p aria-live="polite" role="status">
                {copyStatus}
            </p>
            {manualCopyText !== null && (
                <div className="space-y-2">
                    <label className="block text-sm" htmlFor="manual-diagnostics">
                        Diagnostics to copy
                    </label>
                    <textarea
                        ref={manualCopyRef}
                        readOnly
                        className="h-64 w-full rounded border border-zinc-600 bg-zinc-950 p-3 font-mono text-sm"
                        id="manual-diagnostics"
                        value={manualCopyText}
                    />
                    <Button
                        variant="secondary"
                        onClick={() => {
                            manualCopyRef.current?.focus();
                            manualCopyRef.current?.select();
                        }}
                    >
                        Select all text
                    </Button>
                </div>
            )}
            <section aria-labelledby="operator-error-heading" className="rounded border border-red-900/70 bg-zinc-900 p-4">
                <h3 className="mb-3 font-semibold" id="operator-error-heading">
                    Error
                </h3>
                <pre className="whitespace-pre-wrap break-words font-mono text-sm">{snapshot.errorText}</pre>
            </section>
            <details className="rounded border border-zinc-700 bg-zinc-900 p-4">
                <summary className="cursor-pointer font-semibold">Full diagnostics and commands</summary>
                <pre className="mt-4 whitespace-pre-wrap break-words font-mono text-sm">{snapshot.diagnosticText}</pre>
            </details>
            <p className="text-sm text-zinc-400">
                The copy page expires {new Date(snapshot.expiresAt * 1000).toLocaleDateString('en-US', { timeZone: 'America/New_York' })}.
                The same diagnostics are attached to the email as diagnostics.txt.
            </p>
        </div>
    );
};
